'use client';

import React from 'react';
import Link from 'next/link';
import { ArrowRight, Shield, Zap, Lock, DollarSign, FolderTree, Brain } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface CompareFooterProps {
  /** Current comparison page slug (e.g., 'sanebox', 'superhuman') */
  currentComparison?: string;
}

const comparisons = [
  {
    slug: 'sanebox',
    name: 'SaneBox',
    description: 'Compare AI cold outreach detection and privacy features',
  },
  {
    slug: 'superhuman',
    name: 'Superhuman',
    description: 'Compare pricing and spam detection capabilities',
  },
  {
    slug: 'clean-email',
    name: 'Clean Email',
    description: 'Compare AI detection vs bulk email cleanup',
  },
  {
    slug: 'hey',
    name: 'HEY',
    description: 'Compare Gmail integration vs full email client',
  },
  {
    slug: 'leave-me-alone',
    name: 'Leave Me Alone',
    description: 'Compare real-time filtering vs unsubscribe management',
  },
  {
    slug: 'mailstrom',
    name: 'Mailstrom',
    description: 'Compare AI-powered filtering vs bulk email management',
  },
  {
    slug: 'alfred',
    name: 'alfred_',
    description: 'Compare pricing and cold outreach detection approaches',
  },
  {
    slug: 'gmail',
    name: 'Gmail Spam Filter',
    description: 'See what Gmail misses and what Email Ferret adds',
  },
];

const emailFerretFeatures = [
  {
    icon: Shield,
    title: 'AI Cold Outreach Detection',
    description: 'Catches AI-generated sales emails that traditional filters miss',
  },
  {
    icon: Lock,
    title: 'Privacy-First Design',
    description: 'Email content analyzed in real-time, never stored',
  },
  {
    icon: DollarSign,
    title: 'Affordable Pricing',
    description: '14-day free trial (no card required), then $5/month',
  },
  {
    icon: FolderTree,
    title: 'Smart Folder Organization',
    description: 'AI automatically categorizes emails into folders',
  },
  {
    icon: Brain,
    title: 'Transparent Scoring',
    description: 'See exactly why each email was flagged',
  },
  {
    icon: Zap,
    title: 'Works with Gmail',
    description: 'No client switch required, enhances your existing inbox',
  },
];

const relatedBlogPosts = [
  {
    href: '/blog/why-spam-filters-dont-catch-ai-outreach',
    title: "Why Spam Filters Don't Catch AI Cold Outreach",
  },
  {
    href: '/blog/heuristic-analysis-email-filtering',
    title: 'Heuristic Analysis: The Future of Email Filtering',
  },
  {
    href: '/blog/how-to-identify-ai-cold-outreach',
    title: 'How to Identify AI-Generated Cold Outreach',
  },
  {
    href: '/blog/ai-spam-security-risks',
    title: 'AI Spam Security Risks',
  },
];

export function CompareFooter({ currentComparison }: CompareFooterProps) {
  const otherComparisons = comparisons.filter((c) => c.slug !== currentComparison);

  return (
    <footer className="mt-16 border-t border-gray-700/50">
      {/* Other Comparisons Section */}
      <section className="py-12 border-b border-gray-700/50">
        <h2 className="text-2xl font-bold text-white mb-6">Other Comparisons</h2>
        <div className="grid md:grid-cols-2 gap-6">
          {/* Link to main compare page */}
          <Link
            href="/compare"
            className="group bg-gray-800/50 rounded-xl p-6 border border-gray-700/50 hover:border-blue-500/50 transition-all duration-300"
          >
            <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-blue-400 transition-colors">
              All Comparisons
            </h3>
            <p className="text-sm text-gray-400 mb-3">
              See how Email Ferret compares to other email management tools
            </p>
            <span className="text-blue-400 text-sm font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">
              View all comparisons
              <ArrowRight className="h-4 w-4" />
            </span>
          </Link>

          {/* Other comparison pages */}
          {otherComparisons.map((comparison) => (
            <Link
              key={comparison.slug}
              href={`/compare/${comparison.slug}`}
              className="group bg-gray-800/50 rounded-xl p-6 border border-gray-700/50 hover:border-purple-500/50 transition-all duration-300"
            >
              <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-purple-400 transition-colors">
                Email Ferret vs {comparison.name}
              </h3>
              <p className="text-sm text-gray-400 mb-3">{comparison.description}</p>
              <span className="text-purple-400 text-sm font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                Compare now
                <ArrowRight className="h-4 w-4" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* Why Email Ferret Section */}
      <section className="py-12 border-b border-gray-700/50">
        <h2 className="text-2xl font-bold text-white mb-6">Why Choose Email Ferret?</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {emailFerretFeatures.map((feature) => (
            <div key={feature.title} className="flex items-start gap-3">
              <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <feature.icon className="h-5 w-5 text-blue-400" />
              </div>
              <div>
                <h3 className="font-semibold text-white text-sm mb-1">{feature.title}</h3>
                <p className="text-xs text-gray-400">{feature.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Related Articles Section */}
      <section className="py-12 border-b border-gray-700/50">
        <h2 className="text-2xl font-bold text-white mb-6">Learn More About Email Security</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {relatedBlogPosts.map((post) => (
            <Link
              key={post.href}
              href={post.href}
              className="text-gray-400 hover:text-blue-400 text-sm transition-colors flex items-center gap-2"
            >
              <ArrowRight className="h-3 w-3" />
              {post.title}
            </Link>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12">
        <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-2xl p-8 md:p-12 text-white relative overflow-hidden">
          {/* Background pattern */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:24px_24px] opacity-50" />
          <div className="absolute -top-20 -right-20 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-white/10 rounded-full blur-3xl" />

          <div className="relative z-10 text-center max-w-2xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Try Email Ferret?</h2>
            <p className="text-lg md:text-xl mb-8 opacity-95">
              Start your 14-day free trial—no credit card required. Experience advanced AI cold
              outreach detection, privacy-first email security, and intelligent folder organization.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                size="lg"
                className="bg-white text-gray-900 hover:bg-gray-100 shadow-xl font-semibold"
              >
                <Link href="/login" className="flex items-center gap-2">
                  Start Free Trial — No Card Required
                  <ArrowRight className="h-5 w-5" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white hover:bg-white/20 font-semibold"
              >
                <Link href="/features">View Features</Link>
              </Button>
            </div>
            <p className="mt-6 text-sm opacity-80">
              ✨ No credit card required • 14-day free trial • $5/month after • Cancel anytime
            </p>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-8 border-t border-gray-700/50">
        <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-400">
          <Link href="/" className="hover:text-white transition-colors">
            Home
          </Link>
          <Link href="/features" className="hover:text-white transition-colors">
            Features
          </Link>
          <Link href="/pricing" className="hover:text-white transition-colors">
            Pricing
          </Link>
          <Link href="/blog" className="hover:text-white transition-colors">
            Blog
          </Link>
          <Link href="/compare" className="hover:text-white transition-colors">
            Compare
          </Link>
          <Link href="/privacy" className="hover:text-white transition-colors">
            Privacy
          </Link>
          <Link href="/terms" className="hover:text-white transition-colors">
            Terms
          </Link>
        </div>
        <p className="text-center text-gray-500 text-sm mt-6" suppressHydrationWarning>
          © {new Date().getFullYear()} Email Ferret. All rights reserved.
        </p>
      </section>
    </footer>
  );
}
